import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { ContentManager } from "./ContentManager";
import { SiteSettings } from "./SiteSettings";
import { AccountSettings } from "./AccountSettings";
import { ThemeSettings } from "./ThemeSettings";
import { VisitStats } from "./VisitStats";
import { ThemeToggle } from "../../components/ThemeToggle";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Inbox, Newspaper, Activity, TriangleAlert, Camera, HelpCircle, LogOut, Trash2, Users, Map, Link2, Settings, KeyRound, Palette, Megaphone, Scale, BarChart3 } from "lucide-react";

const tabs = [
  { key: "reports", label: "Laporan Masuk", icon: Inbox },
  { key: "stats", label: "Statistik Kunjungan", icon: BarChart3 },
  { key: "news", label: "Berita & Kegiatan", icon: Newspaper },
  { key: "disasters", label: "Data Bencana", icon: Activity },
  { key: "alerts", label: "Peringatan Dini", icon: TriangleAlert },
  { key: "tickers", label: "Informasi Berjalan", icon: Megaphone },
  { key: "legal-docs", label: "Produk Hukum", icon: Scale },
  { key: "officials", label: "Struktur Organisasi", icon: Users },
  { key: "maps", label: "Peta Risiko", icon: Map },
  { key: "gallery", label: "Galeri", icon: Camera },
  { key: "faqs", label: "FAQ", icon: HelpCircle },
  { key: "links", label: "Tautan Terkait", icon: Link2 },
  { key: "settings", label: "Pengaturan Situs", icon: Settings },
  { key: "theme", label: "Tema Tampilan", icon: Palette },
  { key: "account", label: "Akun & Kata Sandi", icon: KeyRound },
];

const managers = {
  news: {
    name: "news", title: "Berita & Pengumuman", listUrl: "/news",
    fields: [
      { key: "title", label: "Judul", type: "text" },
      { key: "category", label: "Kategori", type: "select", options: ["Berita", "Pengumuman", "Kegiatan", "Siaran Pers"] },
      { key: "image_url", label: "Gambar (opsional)", type: "image" },
      { key: "content", label: "Isi Berita", type: "textarea" },
    ],
    columns: [{ key: "title", label: "Judul" }, { key: "category", label: "Kategori" }],
    sharePath: (item) => `/api/share/berita/${item.id}`,
  },
  disasters: {
    name: "disasters", title: "Data Kejadian Bencana", listUrl: "/disasters",
    fields: [
      { key: "disaster_type", label: "Jenis Bencana", type: "select", options: ["Banjir", "Tanah Longsor", "Kebakaran Hutan & Lahan", "Angin Puting Beliung", "Abrasi Pantai", "Gempa Bumi", "Kekeringan", "Lainnya"] },
      { key: "location", label: "Lokasi", type: "text" },
      { key: "date", label: "Tanggal (YYYY-MM-DD)", type: "text" },
      { key: "status", label: "Status", type: "select", options: ["Penanganan", "Pemantauan", "Selesai"] },
      { key: "affected", label: "Jiwa Terdampak", type: "number" },
      { key: "damage", label: "Kerusakan/Dampak", type: "text" },
      { key: "description", label: "Uraian", type: "textarea" },
    ],
    columns: [{ key: "date", label: "Tanggal" }, { key: "disaster_type", label: "Jenis" }, { key: "location", label: "Lokasi" }, { key: "status", label: "Status" }],
    sharePath: (item) => `/api/share/bencana/${item.id}`,
    shareTitle: (item) => `${item.disaster_type} — ${item.location}`,
  },
  alerts: {
    name: "alerts", title: "Peringatan Dini", listUrl: "/admin/alerts",
    fields: [
      { key: "title", label: "Judul Peringatan", type: "text" },
      { key: "level", label: "Level", type: "select", options: ["Waspada", "Siaga", "Awas"] },
      { key: "source", label: "Sumber", type: "text" },
      { key: "active", label: "Status Aktif", type: "select", options: [true, false], optionLabels: ["Aktif", "Nonaktif"] },
      { key: "message", label: "Pesan", type: "textarea" },
    ],
    columns: [{ key: "title", label: "Judul" }, { key: "level", label: "Level" }, { key: "active", label: "Aktif", render: (v) => (v ? "Ya" : "Tidak") }],
    sharePath: (item) => `/api/share/peringatan/${item.id}`,
  },
  gallery: {
    name: "gallery", title: "Galeri Kegiatan", listUrl: "/gallery",
    fields: [
      { key: "title", label: "Judul Foto", type: "text" },
      { key: "image_url", label: "Gambar", type: "image" },
      { key: "description", label: "Keterangan", type: "textarea" },
    ],
    columns: [{ key: "title", label: "Judul" }, { key: "image_url", label: "URL" }],
    sharePath: (item) => `/api/share/galeri/${item.id}`,
  },
  faqs: {
    name: "faqs", title: "FAQ", listUrl: "/faqs",
    fields: [
      { key: "question", label: "Pertanyaan", type: "text" },
      { key: "answer", label: "Jawaban", type: "textarea" },
    ],
    columns: [{ key: "question", label: "Pertanyaan" }],
  },
  officials: {
    name: "officials", title: "Struktur Organisasi / Pejabat", listUrl: "/officials",
    fields: [
      { key: "position", label: "Jabatan", type: "text" },
      { key: "name", label: "Nama Pejabat", type: "text" },
      { key: "order", label: "Urutan Tampil (angka)", type: "number" },
    ],
    columns: [{ key: "order", label: "No" }, { key: "position", label: "Jabatan" }, { key: "name", label: "Nama" }],
  },
  maps: {
    name: "maps", title: "Peta Risiko & Wilayah", listUrl: "/maps",
    fields: [
      { key: "title", label: "Judul Peta", type: "text" },
      { key: "category", label: "Kategori", type: "select", options: ["Peta Rawan Bencana", "Peta Jalur Evakuasi", "Titik Kumpul & Posko", "Lainnya"] },
      { key: "image_url", label: "Gambar Peta", type: "image" },
      { key: "description", label: "Keterangan", type: "textarea" },
    ],
    columns: [{ key: "title", label: "Judul" }, { key: "category", label: "Kategori" }],
    sharePath: (item) => `/api/share/peta/${item.id}`,
  },
  links: {
    name: "links", title: "Tautan Terkait", listUrl: "/links",
    fields: [
      { key: "name", label: "Nama Instansi/Situs", type: "text" },
      { key: "url", label: "Alamat URL (https://...)", type: "text" },
      { key: "description", label: "Keterangan", type: "text" },
    ],
    columns: [{ key: "name", label: "Nama" }, { key: "url", label: "URL" }],
  },
  tickers: {
    name: "tickers", title: "Informasi Berjalan (teks di bar atas situs)", listUrl: "/admin/tickers",
    fields: [
      { key: "text", label: "Teks Informasi", type: "textarea" },
      { key: "active", label: "Status", type: "select", options: [true, false], optionLabels: ["Aktif", "Nonaktif"] },
    ],
    columns: [{ key: "text", label: "Teks" }, { key: "active", label: "Aktif", render: (v) => (v ? "Ya" : "Tidak") }],
  },
  "legal-docs": {
    name: "legal-docs", title: "Produk Hukum (dokumen dapat diunduh publik)", listUrl: "/legal-docs",
    fields: [
      { key: "title", label: "Judul Produk Hukum", type: "text" },
      { key: "category", label: "Kategori", type: "select", options: ["Undang-Undang", "Peraturan Pemerintah", "Peraturan Daerah", "Peraturan Bupati", "Keputusan Kepala Daerah", "Perka BNPB", "SOP / Pedoman Teknis", "Lainnya"] },
      { key: "year", label: "Tahun", type: "text" },
      { key: "file_url", label: "File Dokumen (PDF/Word)", type: "file" },
      { key: "description", label: "Keterangan", type: "textarea" },
    ],
    columns: [{ key: "title", label: "Judul" }, { key: "category", label: "Kategori" }, { key: "year", label: "Tahun" }],
    sharePath: (item) => `/api/share/produk-hukum/${item.id}`,
  },
};

const statusStyle = {
  Baru: "bg-red-100 text-red-700 border-red-200",
  Diverifikasi: "bg-amber-100 text-amber-700 border-amber-200",
  Ditindaklanjuti: "bg-blue-100 text-blue-700 border-blue-200",
  Selesai: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

const ReportsPanel = () => {
  const [reports, setReports] = useState([]);
  const load = useCallback(() => {
    api.get("/admin/reports").then((r) => setReports(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/reports/${id}`, { status });
      toast.success("Status laporan diperbarui");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus laporan ini?")) return;
    await api.delete(`/admin/reports/${id}`);
    toast.success("Laporan dihapus");
    load();
  };

  const counts = ["Baru", "Diverifikasi", "Ditindaklanjuti", "Selesai"].map((s) => ({
    status: s,
    n: reports.filter((r) => r.status === s).length,
  }));

  return (
    <div data-testid="admin-reports-panel">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {counts.map((c) => (
          <div key={c.status} className="bg-white border border-slate-200 rounded-xl p-5 hover-lift" data-testid={`report-count-${c.status.toLowerCase()}`}>
            <div className="flex items-center justify-between">
              <p className="font-mono text-3xl font-bold text-slate-900">{c.n}</p>
              <span className={`w-2.5 h-2.5 rounded-full ${statusStyle[c.status]?.split(" ")[0].replace("bg-", "bg-").replace("-100", "-500")}`} />
            </div>
            <p className={`section-label mt-2 ${statusStyle[c.status]?.split(" ")[1] || "text-slate-500"}`}>{c.status}</p>
          </div>
        ))}
      </div>
      {reports.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded p-10 text-center text-slate-400" data-testid="reports-empty">Belum ada laporan masuk dari masyarakat.</div>
      ) : (
        <div className="space-y-4">
          {reports.map((r) => (
            <div key={r.id} className="border border-slate-200 bg-white rounded-lg p-5 hover-lift" data-testid={`report-item-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="font-mono text-[11px] uppercase bg-orange-100 text-orange-700 px-2 py-0.5 rounded">{r.disaster_type}</span>
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded border ${statusStyle[r.status] || "bg-slate-100 text-slate-600"}`}>{r.status}</span>
                    <span className="font-mono text-xs text-slate-400">{new Date(r.created_at).toLocaleString("id-ID")}</span>
                  </div>
                  <p className="font-heading font-semibold text-slate-900">{r.location}</p>
                  <p className="text-sm text-slate-600 mt-1">{r.description}</p>
                  <p className="text-xs text-slate-500 mt-2 font-mono">Pelapor: {r.name} · {r.phone}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={r.status} onValueChange={(v) => setStatus(r.id, v)}>
                    <SelectTrigger className="w-36" data-testid={`report-status-select-${r.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["Baru", "Diverifikasi", "Ditindaklanjuti", "Selesai"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button variant="ghost" size="icon" onClick={() => remove(r.id)} data-testid={`report-delete-${r.id}`}>
                    <Trash2 size={16} className="text-red-500" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const navGroups = [
  { label: "Operasional", keys: ["reports", "stats"] },
  { label: "Konten", keys: ["news", "disasters", "alerts", "tickers", "legal-docs"] },
  { label: "Profil & Media", keys: ["officials", "maps", "gallery", "faqs", "links"] },
  { label: "Pengaturan", keys: ["settings", "theme", "account"] },
];

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("reports");

  useEffect(() => {
    if (!loading && !user) navigate("/admin/login");
  }, [loading, user, navigate]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Memuat...</div>;
  if (!user) return null;

  const activeTab = tabs.find((t) => t.key === tab);

  return (
    <div className="min-h-screen bg-slate-100 lg:flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="lg:w-64 lg:shrink-0 bg-slate-950 text-white lg:min-h-screen lg:sticky lg:top-0 lg:h-screen flex flex-col" data-testid="admin-sidebar">
        <div className="flex items-center gap-3 px-5 h-16 border-b border-slate-800/80">
          <img src="/logo-bpbd.png" alt="Logo BPBD" className="w-9 h-9 object-contain" />
          <div className="leading-tight">
            <p className="font-heading font-bold text-sm">Panel Admin</p>
            <p className="text-[11px] text-slate-500">BPBD Kayong Utara</p>
          </div>
        </div>
        <nav className="flex lg:flex-col gap-0.5 overflow-x-auto lg:overflow-y-auto px-3 py-4 flex-1">
          {navGroups.map((g) => (
            <div key={g.label} className="lg:mb-4 flex lg:block gap-0.5">
              <p className="hidden lg:block section-label text-slate-600 px-3 mb-2">{g.label}</p>
              {g.keys.map((key) => {
                const t = tabs.find((x) => x.key === key);
                return (
                  <button
                    key={t.key}
                    onClick={() => setTab(t.key)}
                    data-testid={`admin-tab-${t.key}`}
                    className={`admin-nav-item ${tab === t.key ? "active" : ""} w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-semibold whitespace-nowrap ${
                      tab === t.key ? "bg-orange-600/15 text-orange-400" : "text-slate-400 hover:bg-slate-900 hover:text-white"
                    }`}
                  >
                    <t.icon size={17} /> {t.label}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="hidden lg:block px-5 py-4 border-t border-slate-800/80">
          <p className="font-mono text-[11px] text-slate-500 truncate">{user.email}</p>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0">
        <header className="bg-white/85 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-40">
          <div className="px-6 h-16 flex items-center justify-between">
            <div>
              <p className="section-label text-slate-400 hidden sm:block">Dashboard</p>
              <h1 className="font-heading font-bold text-slate-900 text-lg leading-tight">{activeTab?.label}</h1>
            </div>
            <div className="flex items-center gap-4">
              <ThemeToggle />
              <a href="/" target="_blank" rel="noreferrer" className="text-sm font-semibold text-sky-600 hover:text-orange-600 transition-colors hidden sm:block" data-testid="admin-view-site">
                Lihat Situs ↗
              </a>
              <button onClick={async () => { await logout(); navigate("/admin/login"); }} className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-red-600 transition-colors" data-testid="admin-logout-btn">
                <LogOut size={15} /> Keluar
              </button>
            </div>
          </div>
        </header>
        <main className="p-6">
          {tab === "reports" ? <ReportsPanel /> : tab === "stats" ? <VisitStats /> : tab === "settings" ? <SiteSettings /> : tab === "theme" ? <ThemeSettings /> : tab === "account" ? <AccountSettings /> : <ContentManager key={tab} config={managers[tab]} />}
        </main>
      </div>
    </div>
  );
}
